import { useCallback, useState } from "react";
import { PushSubscriptionPayload } from "types/PushSubscriptionTypes";
import { useCreatePushSubscription } from "./CreatePushSubscriptionHook";

interface UsePushNotifications {
  isSupported: boolean;
  permission: NotificationPermission | "unsupported";
  ensureSubscribed: () => Promise<boolean>;
}

const isPushSupported = (): boolean =>
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export default function usePushNotifications(): UsePushNotifications {
  const [isSupported] = useState<boolean>(isPushSupported());
  const [permission, setPermission] = useState<
    NotificationPermission | "unsupported"
  >(isPushSupported() ? Notification.permission : "unsupported");
  const { mutateAsync: createPushSubscription } = useCreatePushSubscription();

  const ensureSubscribed = useCallback(async (): Promise<boolean> => {
    if (!isSupported) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const existingSubscription =
        await registration.pushManager.getSubscription();

      if (existingSubscription) {
        return true;
      }

      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== "granted") {
        return false;
      }

      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(
          process.env.REACT_APP_VAPID_PUBLIC_KEY as string
        ),
      });

      await createPushSubscription(
        subscription.toJSON() as unknown as PushSubscriptionPayload
      );

      return true;
    } catch (_e) {
      return false;
    }
  }, [isSupported, createPushSubscription]);

  return { isSupported, permission, ensureSubscribed };
}
